import { StorefrontIcon } from "@/components/storefront-icon";
import { VideoTextSection } from "@/components/video-text-section";
import { HeroSliderSection } from "@/components/hero-slider-section";
import { LondonBoroughsSection } from "@/components/london-boroughs-section";
import { FrozenDeliveryQuotePage } from "@/components/frozen-delivery-quote-page";
import { QuickSignInSection } from "@/components/quick-sign-in-section";
import {
  CustomerStoriesSection,
  ProductSpotlightSection,
  PromoBandSection,
  ScrollingTickerSection,
  ShowcaseSection,
  StatsBandSection,
  WeeklyHighlightsSection,
} from "@/components/content-blocks";
import { displayFont } from "@/components/content-blocks/band-helpers";
import type { ContentBlock } from "@/lib/storefront-types";

type ContentPageBlocksProps = {
  blocks: ContentBlock[];
  pageAppearance?: "light" | "dark" | "frozen";
  frozenDeliveryCalculatorHeadingLevel?: 1 | 2;
  heroHeadingLevel?: 1 | 2;
};

type SectionStackPosition = "single" | "start" | "middle" | "end";

function CenteredContentBlockFrame({
  children,
}: {
  children: React.ReactNode;
}) {
  return <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">{children}</div>;
}

function getSectionStackPosition(
  index: number,
  total: number,
): SectionStackPosition {
  if (total <= 1) {
    return "single";
  }

  if (index === 0) {
    return "start";
  }

  if (index === total - 1) {
    return "end";
  }

  return "middle";
}

function AccordionBlockSection({
  block,
}: {
  block: Extract<ContentBlock, { kind: "accordion" }>;
}) {
  const isLight = block.appearance === "light";
  const isFrozen = block.appearance === "frozen";
  const sectionClass = isLight
    ? "border storefront-border-soft bg-white shadow-[0_24px_50px_-36px_rgba(0,39,0,0.35)]"
    : isFrozen
      ? "border border-white/12 storefront-surface-frozen shadow-[0_24px_56px_-34px_rgba(0,0,0,0.52)]"
      : "border border-white/10 storefront-surface-dark-alt shadow-[0_24px_56px_-34px_rgba(0,0,0,0.44)]";
  const headerClass = isLight
    ? "border-b storefront-border-soft"
    : isFrozen
      ? "border-b storefront-border-primary-soft"
      : "border-b border-white/10";
  const introClass = isLight
    ? "storefront-text-body"
    : isFrozen
      ? "text-white/78"
      : "text-white/72";
  const dividerClass = isLight ? "divide-y divide-[#e1e9dd]" : "divide-y divide-white/10";
  const summaryTitleClass = isLight ? "storefront-text-heading" : "text-white";
  const bubbleClass = isLight
    ? "storefront-border-primary-soft storefront-bg-primary-soft storefront-text-primary"
    : isFrozen
      ? "storefront-border-primary-soft storefront-surface-frozen-soft storefront-text-primary-tint"
      : "border-white/12 bg-white/8 storefront-text-primary-tint";
  const answerClass = isLight
    ? "storefront-text-body"
    : isFrozen
      ? "text-white/78"
      : "text-white/72";

  return (
    <section className={`overflow-hidden rounded-[2rem] ${sectionClass}`}>
      <div className={`px-6 py-6 sm:px-8 ${headerClass}`}>
        {block.sectionTitle ? (
          <h2
            className={`${displayFont.className} text-2xl font-semibold tracking-tight sm:text-[2rem] ${
              isLight ? "storefront-text-heading" : "text-white"
            }`}
          >
            {block.sectionTitle}
          </h2>
        ) : null}
        {block.introHtml ? (
          <div
            className={`mt-3 max-w-3xl space-y-4 text-base leading-7 ${introClass}`}
            dangerouslySetInnerHTML={{ __html: block.introHtml }}
          />
        ) : null}
      </div>

      <div className={dividerClass}>
        {block.accordionItems.map((item, index) => (
          <details
            key={`${item.title}-${index}`}
            open={index === 0}
            className="group px-5 py-4 sm:px-7"
          >
            <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-left">
              <span className={`text-lg font-semibold ${summaryTitleClass}`}>
                {item.title}
              </span>
              <span
                className={`mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-transform group-open:rotate-45 ${bubbleClass}`}
              >
                +
              </span>
            </summary>
            <div
              className={`mt-4 max-w-3xl space-y-4 pl-0 text-[0.98rem] leading-7 ${answerClass}`}
              dangerouslySetInnerHTML={{ __html: item.answerHtml }}
            />
          </details>
        ))}
      </div>
    </section>
  );
}

function FeatureHighlightsSection({
  block,
}: {
  block: Extract<ContentBlock, { kind: "featureHighlights" }>;
}) {
  const isLight = block.appearance === "light";
  const isFrozen = block.appearance === "frozen";
  const bandClass = isLight
    ? "border-y storefront-border-soft bg-[#f8fbf5]"
    : isFrozen
      ? "border-y border-white/12 storefront-surface-frozen"
      : "border-y border-white/10 storefront-surface-dark";
  const cardClass = isLight
    ? "border storefront-border-soft bg-white shadow-[0_20px_44px_-38px_rgba(0,39,0,0.32)]"
    : isFrozen
      ? "border storefront-border-primary-soft bg-white/8 shadow-[0_20px_44px_-34px_rgba(0,0,0,0.4)] backdrop-blur-sm"
      : "border border-white/10 bg-white/6 shadow-[0_20px_44px_-34px_rgba(0,0,0,0.34)] backdrop-blur-sm";
  const descriptionClass = isLight
    ? "storefront-text-body"
    : isFrozen
      ? "text-white/76"
      : "text-white/68";

  return (
    <section className={`relative overflow-hidden ${bandClass}`}>
      <div className="storefront-soft-radial-top-right pointer-events-none absolute inset-0" />
      <div className="relative mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:py-12">
        {block.sectionTitle ? (
          <h2
            className={`${displayFont.className} mb-6 text-2xl font-semibold tracking-tight sm:text-[2rem] ${
              isLight ? "storefront-text-heading" : "text-white"
            }`}
          >
            {block.sectionTitle}
          </h2>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {block.featureItems.map((item) => (
            <article
              key={item.title}
              className={`flex h-full flex-col rounded-[1.6rem] px-5 py-6 ${cardClass}`}
            >
              <span className="storefront-bg-primary inline-flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-[0_12px_22px_-16px_rgba(0,39,0,0.6)]">
                <StorefrontIcon
                  asset={item.icon}
                  context="feature"
                  label={item.title}
                  className="h-7 w-7"
                />
              </span>
              <h3
                className={`mt-5 text-base font-semibold uppercase tracking-[0.16em] ${
                  isLight ? "storefront-text-heading" : "text-white"
                }`}
              >
                {item.title}
              </h3>
              <p className={`mt-3 text-sm leading-6 ${descriptionClass}`}>
                {item.description}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function getBlockKey(block: ContentBlock, index: number) {
  if ("sectionTitle" in block && block.sectionTitle) {
    return `${block.kind}-${block.sectionTitle}`;
  }

  if ("headline" in block && block.headline) {
    return `${block.kind}-${block.headline}`;
  }

  return `${block.kind}-${index}`;
}

function applyAppearanceOverride(
  block: ContentBlock,
  pageAppearance?: "light" | "dark" | "frozen",
): ContentBlock {
  if (!pageAppearance || !("appearance" in block)) {
    return block;
  }

  return { ...block, appearance: pageAppearance } as ContentBlock;
}

function renderBlock(
  block: ContentBlock,
  index: number,
  total: number,
  pageAppearance?: "light" | "dark" | "frozen",
  frozenDeliveryCalculatorHeadingLevel: 1 | 2 = 2,
  heroHeadingLevel: 1 | 2 = 2,
) {
  const key = getBlockKey(block, index);
  const stackPosition = getSectionStackPosition(index, total);

  switch (block.kind) {
    case "accordion":
      return (
        <CenteredContentBlockFrame key={key}>
          <AccordionBlockSection
            block={
              applyAppearanceOverride(block, pageAppearance) as Extract<
                ContentBlock,
                { kind: "accordion" }
              >
            }
          />
        </CenteredContentBlockFrame>
      );
    case "featureHighlights":
      return (
        <FeatureHighlightsSection
          key={key}
          block={
            applyAppearanceOverride(block, pageAppearance) as Extract<
              ContentBlock,
              { kind: "featureHighlights" }
            >
          }
        />
      );
    case "videoText":
      return (
        <VideoTextSection
          key={key}
          block={block}
          stackPosition={stackPosition}
        />
      );
    case "heroSlider":
      return (
        <HeroSliderSection
          key={key}
          block={block}
          headingLevel={heroHeadingLevel}
        />
      );
    case "londonBoroughs":
      return (
        <LondonBoroughsSection
          key={key}
          block={block}
          stackPosition={stackPosition}
        />
      );
    case "promoBand":
      return <PromoBandSection key={key} block={block} />;
    case "weeklyHighlights":
      return <WeeklyHighlightsSection key={key} block={block} />;
    case "customerStories":
      return <CustomerStoriesSection key={key} block={block} />;
    case "scrollingTicker":
      return <ScrollingTickerSection key={key} block={block} />;
    case "statsBand":
      return <StatsBandSection key={key} block={block} />;
    case "productSpotlight":
      return (
        <ProductSpotlightSection
          key={key}
          block={
            applyAppearanceOverride(block, pageAppearance) as Extract<
              ContentBlock,
              { kind: "productSpotlight" }
            >
          }
        />
      );
    case "frozenDeliveryCalculator":
      return (
        <FrozenDeliveryQuotePage
          key={key}
          bodyHtml={block.bodyHtml}
          eyebrow={block.eyebrow}
          headingLevel={frozenDeliveryCalculatorHeadingLevel}
          headline={block.headline}
          image={block.image}
          pricePerBox={block.pricePerBox}
        />
      );
    case "quickSignIn":
      return <QuickSignInSection key={key} block={block} />;
    case "showcase":
      return (
        <ShowcaseSection
          key={key}
          block={
            applyAppearanceOverride(block, pageAppearance) as Extract<
              ContentBlock,
              { kind: "showcase" }
            >
          }
        />
      );
    default:
      return null;
  }
}

export function ContentPageBlocks({
  blocks,
  frozenDeliveryCalculatorHeadingLevel = 2,
  pageAppearance,
  heroHeadingLevel = 2,
}: ContentPageBlocksProps) {
  // Only the first hero slider may carry the page H1 — a second hero block on
  // the same page must not introduce another one.
  const firstHeroSliderIndex = blocks.findIndex(
    (block) => block.kind === "heroSlider",
  );

  return (
    <div>
      {blocks.map((block, index) =>
        renderBlock(
          block,
          index,
          blocks.length,
          pageAppearance,
          frozenDeliveryCalculatorHeadingLevel,
          index === firstHeroSliderIndex ? heroHeadingLevel : 2,
        ),
      )}
    </div>
  );
}
